import { buttonStyle } from 'components/buttons/buttonStyle';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput.tsx';
import { DataTable } from 'components/tables/data-table';
import { Button } from 'components/ui/button';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { useGetAllOfficesQuery } from 'store/api/offices.ts';
import { useUpdatePrimaryOfficeForCasesMutation } from 'store/api/tenants.ts';
import { FeatureFlag } from 'types/features.ts';
import { Office } from 'types/offices.ts';
import { PrimaryOfficeForCasesUpdateDto, TenantConfigurationDto } from 'types/tenants.ts';
import { t } from 'utils/getTranslations.ts';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';

interface Props {
    tenantConfiguration: TenantConfigurationDto;
}

interface PrimaryOfficeForCasesForm {
    primaryOfficeForCases: Office | null;
}

const PrimaryOfficeForCasesControl = ({ tenantConfiguration }: Props): JSX.Element => {
    const [updatePrimaryOfficeForCases] = useUpdatePrimaryOfficeForCasesMutation();
    const { data: offices, isLoading: isLoadingOffices } = useGetAllOfficesQuery();
    const isConfigurationUpdateEnabled = useIsFeatureEnabled(FeatureFlag.HANDLED_BY_OFFICE_CONTROL);
    const hasUpdatePermission = useHasPermissions(['UPDATE.BACK_OFFICE_SETTINGS']);

    const selectedOffice = useMemo(() => {
        if (!tenantConfiguration.primaryOfficeForCasesId || !offices) {
            return null;
        }
        return offices.find((office) => office.id === tenantConfiguration.primaryOfficeForCasesId) || null;
    }, [tenantConfiguration.primaryOfficeForCasesId, offices]);

    // RHF `values` keeps the form synced when `selectedOffice` recomputes from async data,
    // replacing a useEffect(setValue) bridge.
    const { control, setValue, handleSubmit } = useForm<PrimaryOfficeForCasesForm>({
        defaultValues: {
            primaryOfficeForCases: selectedOffice,
        },
        values: { primaryOfficeForCases: selectedOffice },
    });

    const columns = useMemo<DataTableColumnDef<TenantConfigurationDto>[]>(
        () => [
            {
                header: t('configuration.primary-office-for-cases'),
                id: 'primary-office-for-cases',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 340,
                },
                cell: () => (
                    <div className="flex gap-4">
                        <SelectInput<PrimaryOfficeForCasesForm, Office>
                            control={control}
                            name="primaryOfficeForCases"
                            options={offices || []}
                            getOptionLabel="name"
                            getOptionIdentifier="id"
                            setValue={setValue}
                            clearable
                            disabled={isLoadingOffices || !hasUpdatePermission}
                        />
                        <Button
                            {...buttonStyle({ variant: 'contained', color: 'secondary' }, 'h-9')}
                            onClick={() => {
                                void handleSubmit((data) => {
                                    const updateDto: PrimaryOfficeForCasesUpdateDto = {
                                        primaryOfficeForCasesId: data.primaryOfficeForCases?.id ?? null,
                                    };
                                    updatePrimaryOfficeForCases(updateDto);
                                })();
                            }}
                            disabled={!isConfigurationUpdateEnabled || !hasUpdatePermission}
                        >
                            <FormattedMessage id="save" />
                        </Button>
                    </div>
                ),
            },
            {
                header: '',
                id: 'spacer',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: () => null,
            },
        ],
        [
            control,
            setValue,
            handleSubmit,
            offices,
            isLoadingOffices,
            hasUpdatePermission,
            isConfigurationUpdateEnabled,
            updatePrimaryOfficeForCases,
        ]
    );

    const rows = useMemo(() => [tenantConfiguration], [tenantConfiguration]);

    return (
        <>
            <div className="flex justify-between">
                <h3 className="text-h3 text-balance">
                    <FormattedMessage id="configuration.primary-office-for-cases" />
                </h3>
            </div>
            <DataTable<TenantConfigurationDto>
                columns={columns}
                data={rows}
                columnConfigId="configuration-primary-office-for-cases"
            />
        </>
    );
};

export default PrimaryOfficeForCasesControl;
